<template>
  <div ref="surface" class="workspace-route-page">
    <component :is="view" :key="viewKey" />
  </div>
</template>

<script setup>
/* global defineProps */
import { computed, inject, nextTick, onActivated, onBeforeUnmount, onDeactivated, provide, ref, shallowReactive, watch } from 'vue'
import { onBeforeRouteLeave, routeLocationKey, routerViewLocationKey } from 'vue-router'
import { workspaceActivityKey, workspaceNavigationKey } from './navigation'
import { routeScriptId } from './scriptSelection'

const props = defineProps({ view: { type: Object, required: true }, location: { type: Object, required: true } })
const surface = ref(null)
const active = ref(true)
const navigation = inject(workspaceNavigationKey, null)
const location = shallowReactive({ ...props.location })
// ImageGenerate already resets script-specific UI through its route watcher.
// Keep its task queue/uploader alive when another script becomes selected.
const viewKey = computed(() => ['ImageGenerate', 'ImageGenerateStudio'].includes(location.name) ? 'image' : routeScriptId(location))
let scrollPositions = []
let windowPosition = null
let restoreFrame = 0
// Cached pages must not observe another tool's params or nested router. Otherwise
// leaving /videos/studio/42 clears the video form as soon as scriptId disappears.
provide(routeLocationKey, location)
provide(routerViewLocationKey, computed(() => location))
provide(workspaceActivityKey, computed(() => active.value && !navigation?.drawerOpen.value))
watch(() => props.location, value => Object.assign(location, value))
// Detached DOM can report zero scroll offsets. Capture before the router moves
// it into KeepAlive storage, and restore after child activation/layout hooks.
onBeforeRouteLeave(() => {
  windowPosition = { top: window.scrollY, left: window.scrollX }
  scrollPositions = [...(surface.value?.querySelectorAll('*') || [])]
    .filter(element => element.scrollTop || element.scrollLeft)
    .map(element => ({ element, top: element.scrollTop, left: element.scrollLeft }))
})
onActivated(async () => {
  active.value = true
  await nextTick()
  restoreFrame = requestAnimationFrame(() => {
    if (!active.value) return
    for (const { element, top, left } of scrollPositions) element.scrollTo({ top, left, behavior: 'instant' })
    if (windowPosition) window.scrollTo({ ...windowPosition, behavior: 'instant' })
  })
})
onDeactivated(() => {
  active.value = false
  cancelAnimationFrame(restoreFrame)
  surface.value?.querySelectorAll('audio, video').forEach(media => media.pause())
})
onBeforeUnmount(() => { cancelAnimationFrame(restoreFrame); scrollPositions = [] })
</script>

<style scoped>
.workspace-route-page { min-width: 0; min-height: 100vh; }
</style>
